import { useEffect, useState } from "react";
import { BellRing, CalendarCheck, CheckCircle2, Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DeclareDialog } from "@/components/DeclareDialog";
import { useFilters } from "@/context/FilterContext";
import { api, cleanParams, download, errMsg, money } from "@/lib/api";

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const longDate = (iso) => { const [y, m, d] = iso.split("-"); return `${Number(d)} ${MOIS[Number(m) - 1]} ${y}`; };

export const DeclarationBanner = ({ refreshKey }) => {
  const { filters } = useFilters();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [declare, setDeclare] = useState(null);
  const [tick, setTick] = useState(0);
  const params = cleanParams({ plateforme: filters.plateforme, logement: filters.logement });

  useEffect(() => {
    api.get("/declaration", { params }).then((r) => setD(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.plateforme, filters.logement, refreshKey, tick]);

  if (!d) return null;
  const tone = d.urgent ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-white";
  const Icon = d.urgent ? BellRing : CalendarCheck;
  const dl = async () => {
    setLoading(true);
    try { await download("/exports/trimestriel", { ...params, annee: d.annee, trimestre: d.trimestre }, `rapport_${d.annee}-T${d.trimestre}.xlsx`); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <div className={`mb-6 rounded-lg border p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4 rise ${tone}`} data-testid="declaration-banner">
      <div className="flex items-start gap-3 flex-1">
        <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${d.urgent ? "text-amber-600" : "text-emerald-700"}`} />
        <div>
          <p className="text-sm font-semibold" data-testid="declaration-title">
            Déclaration TPS/TVQ · T{d.trimestre} {d.annee} {{ en_cours: "(trimestre en cours)", a_declarer: "(à produire)", a_venir: "(prochain trimestre)" }[d.statut]}
          </p>
          <p className="text-xs text-slate-600 mt-0.5" data-testid="declaration-deadline">
            Période du {longDate(d.periode_debut)} au {longDate(d.periode_fin)} · échéance le <b>{longDate(d.echeance)}</b>
            {" "}— {d.jours_restants >= 0 ? `dans ${d.jours_restants} jour(s)` : "dépassée"}
          </p>
          {d.derniere_declaration && (
            <p className="text-xs text-emerald-700 mt-0.5" data-testid="declaration-last">
              Dernière déclaration : T{d.derniere_declaration.trimestre} {d.derniere_declaration.annee}, le {longDate(d.derniere_declaration.date_declaration)} · {money(d.derniere_declaration.total)}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">TPS</p><p className="tabular" data-testid="declaration-tps">{money(d.tps)}</p></div>
        <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">TVQ</p><p className="tabular" data-testid="declaration-tvq">{money(d.tvq)}</p></div>
        <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">À déclarer</p><p className="tabular font-bold" data-testid="declaration-total">{money(d.total)}</p></div>
        <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Non remis</p><p className={`tabular ${d.ecart > 0.05 ? "text-red-600" : ""}`} data-testid="declaration-ecart">{money(d.ecart)}</p></div>
        <div title={`Taxe sur l'hébergement 3,5 % à remettre par vous (hors taxe remise par Airbnb : ${money(d.th_remise_plateforme || 0)}${d.th_remise_booking ? ` et par Booking : ${money(d.th_remise_booking)}` : ""})`}>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Taxe de séjour</p>
          <p className="tabular font-semibold text-amber-700" data-testid="declaration-th">{money(d.th_a_remettre || 0)}</p>
          <p className="text-[10px] text-slate-500" data-testid="declaration-th-detail">dont perçue {money(d.th_percue || 0)} · non perçue {money(d.th_non_percue || 0)}</p>
        </div>
        <Button size="sm" variant="outline" onClick={dl} disabled={loading} className="bg-white" data-testid="declaration-download-button">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Rapport Excel
        </Button>
        <Button size="sm" variant="outline" className="bg-white" data-testid="declaration-download-pdf"
          onClick={() => download("/declaration-taxes/rapport", { annee: d.annee, trimestre: d.trimestre, format: "pdf" }, `Déclaration TPS-TVQ T${d.trimestre} ${d.annee}.pdf`).catch((e) => toast.error(errMsg(e)))}>
          <FileText className="h-4 w-4 mr-1" />Rapport PDF
        </Button>
        <Button size="sm" onClick={() => setDeclare({ annee: d.annee, trimestre: d.trimestre, tps: d.tps, tvq: d.tvq })} className="bg-emerald-700 hover:bg-emerald-800" data-testid="declaration-mark-button">
          <CheckCircle2 className="h-4 w-4 mr-1" />Marquer comme déclaré
        </Button>
      </div>
      <DeclareDialog target={declare} onClose={() => setDeclare(null)} onDone={() => { setDeclare(null); setTick((t) => t + 1); }} />
    </div>
  );
};
